import { useEffect, useMemo, useState } from 'react';
import {
  marketplaceApi,
  type CatalogKind,
  type Category,
  type PaymentOptions,
  type ProductCard,
} from '../api/marketplace';
import { ProductPage } from './ProductPage';
import { SearchFilters, matchesQuery } from '../components/SearchFilters';
import { StarIcon } from '../features/tabs/icons';
import { TopUpBlock } from '../features/topup/TopUpBlock';
import { ErrorState, Loader } from '../components/States';
import { isTopUpProduct } from '../lib/top-up';
import { pricedFrom, selectedEdition } from '../lib/editions';
import { RatingStars } from '../features/reviews/ReviewsBlock';
import './CatalogPage.css';

export const CATALOG_SHELVES: Array<{ id: CatalogKind | undefined; title: string; subtitle: string }> = [
  { id: undefined, title: 'Все товары', subtitle: '' },
  { id: 'keys', title: 'Ключи', subtitle: 'Лицензии и коды активации' },
  { id: 'subscriptions', title: 'Подписки', subtitle: 'Промокоды и продления' },
  { id: 'services', title: 'Услуги', subtitle: 'Разовые работы под запрос' },
  { id: 'deals', title: 'Акции', subtitle: 'Товары со скидкой' },
];

type CatalogSort = 'newest' | 'oldest' | 'price_asc' | 'price_desc' | 'title';

interface CatalogPageProps {
  kind?: CatalogKind;
  onKindChange: (kind?: CatalogKind) => void;
  payments: PaymentOptions;
  cartCloseToken: number;
  onOpenCart: () => void;
  onPurchased: (orderId?: string) => void;
}

export function CatalogPage({ kind, onKindChange, payments, cartCloseToken, onOpenCart, onPurchased }: CatalogPageProps) {
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [opened, setOpened] = useState<{ id: string; editionId?: string } | null>(null);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<CatalogSort>('newest');
  const [stock, setStock] = useState('all');
  const [sale, setSale] = useState('all');
  const [category, setCategory] = useState('');

  useEffect(() => {
    marketplaceApi.categories().then((response) => setCategories(response.items)).catch(() => undefined);
  }, []);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError(null);

    marketplaceApi
      .products({
        kind,
        sort,
        category: category || undefined,
        take: 50,
      })
      .then((response) => {
        if (!cancelled) setProducts(response.items);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setError(cause instanceof Error ? cause.message : 'Не удалось загрузить каталог');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [kind, reloadKey, sort, category]);

  useEffect(() => {
    if (cartCloseToken > 0) setOpened(null);
  }, [cartCloseToken]);

  const visible = useMemo(() => {
    return products.filter((product) => {
      if (!matchesQuery([product.title, product.subtitle, product.description, product.category?.title], search)) {
        return false;
      }
      if (stock === 'in' && !product.inStock) return false;
      if (stock === 'out' && product.inStock) return false;
      if (sale === 'sale' && !product.compareAtStars && !product.compareAtRub) return false;
      return true;
    });
  }, [products, search, stock, sale]);

  const showTopUps = kind === undefined || kind === 'services';
  const featured = showTopUps ? visible.filter((product) => isTopUpProduct(product)) : [];
  const rest = visible.filter((product) => !isTopUpProduct(product));

  const shelf = CATALOG_SHELVES.find((item) => item.id === kind) ?? CATALOG_SHELVES[0]!;

  if (isLoading && products.length === 0 && !search) return <Loader />;
  if (error && products.length === 0 && !search) {
    return <ErrorState message={error} onRetry={() => setReloadKey((key) => key + 1)} />;
  }

  return (
    <div className="catalog">
      <header className="catalog__header">
        <h1 className="page-title">{shelf.title}</h1>
        {kind && shelf.subtitle ? <p className="catalog__subtitle">{shelf.subtitle}</p> : null}
      </header>

      <div className="catalog__chips">
        {CATALOG_SHELVES.map((item) => (
          <button
            key={item.title}
            type="button"
            className={`chip${kind === item.id ? ' chip--active' : ''}`}
            onClick={() => onKindChange(item.id)}
          >
            {item.title}
          </button>
        ))}
      </div>

      {featured.length > 0 ? (
        <section className="catalog__featured">
          <h2 className="catalog__featured-title">Пополнение</h2>
          <TopUpBlock
            products={featured}
            payments={payments}
            onPurchased={(orderId) => {
              onPurchased(orderId);
              setReloadKey((key) => key + 1);
            }}
          />
        </section>
      ) : null}

      <SearchFilters
        search={search}
        onSearch={setSearch}
        placeholder="Найти товар"
        filters={[
          {
            id: 'sort',
            value: sort,
            onChange: (value) => setSort(value as CatalogSort),
            options: [
              { value: 'newest', label: 'Сначала новые' },
              { value: 'oldest', label: 'Сначала старые' },
              { value: 'price_asc', label: 'Дешевле' },
              { value: 'price_desc', label: 'Дороже' },
              { value: 'title', label: 'По названию' },
            ],
          },
          {
            id: 'stock',
            value: stock,
            onChange: setStock,
            options: [
              { value: 'all', label: 'Любое наличие' },
              { value: 'in', label: 'В наличии' },
              { value: 'out', label: 'Нет в наличии' },
            ],
          },
          {
            id: 'sale',
            value: sale,
            onChange: setSale,
            options: [
              { value: 'all', label: 'Все цены' },
              { value: 'sale', label: 'Только акции' },
            ],
          },
          {
            id: 'category',
            value: category,
            onChange: setCategory,
            options: [
              { value: '', label: 'Все категории' },
              ...categories.map((item) => ({ value: item.slug, label: item.title })),
            ],
          },
        ]}
      />

      {error ? <div className="catalog__error">{error}</div> : null}

      {rest.length > 0 ? (
        <div className="catalog__grid">
            {rest.map((product) => (
              <CatalogProductCard
                key={product.id}
                product={product}
                kind={kind}
                onOpen={(id, editionId) => setOpened({ id, editionId })}
              />
            ))}
        </div>
      ) : null}

      {visible.length === 0 && !isLoading ? (
        <p className="catalog__empty">
          {search || stock !== 'all' || sale !== 'all' || category
            ? 'Ничего не нашли по этим фильтрам'
            : 'В каталоге пока ничего нет'}
        </p>
      ) : null}

      {opened ? (
        <ProductPage
          productId={opened.id}
          initialEditionId={opened.editionId}
          payments={payments}
          onClose={() => setOpened(null)}
          onOpenCart={() => {
            setOpened(null);
            onOpenCart();
          }}
          onPurchased={(orderId) => {
            onPurchased(orderId);
            setReloadKey((key) => key + 1);
          }}
        />
      ) : null}
    </div>
  );
}

function CatalogProductCard({
  product,
  kind,
  onOpen,
}: {
  product: ProductCard;
  kind?: CatalogKind;
  onOpen: (id: string, editionId?: string) => void;
}) {
  const editions = product.editions ?? [];
  const [editionId, setEditionId] = useState(editions[0]?.id ?? '');
  const edition = selectedEdition(product, editionId);
  const prices = pricedFrom(product, edition);

  return (
    <article className="product">
      <button type="button" className="product__hit" onClick={() => onOpen(product.id, edition?.id)}>
        <div className="product__cover">
          {product.coverUrl ? (
            <img src={product.coverUrl} alt="" loading="lazy" />
          ) : (
            <span className="product__cover-fallback">{product.title.slice(0, 2).toUpperCase()}</span>
          )}
          {!product.inStock ? <span className="product__badge">Нет в наличии</span> : null}
          {prices.compareAtStars || prices.compareAtRub ? (
            <span className="product__badge product__badge--sale">Акция</span>
          ) : null}
        </div>

        <div className="product__body">
          <h2 className="product__title">{product.title}</h2>
          {product.ratingCount ? (
            <p className="product__rating">
              <RatingStars value={product.ratingAvg ?? 0} size={12} />
              <span>
                {product.ratingAvg} · {product.ratingCount}
              </span>
            </p>
          ) : null}
          {product.subtitle ? <p className="product__subtitle">{product.subtitle}</p> : null}
          {kind && product.description ? <p className="product__excerpt">{product.description}</p> : null}
        </div>
      </button>

      {editions.length > 0 ? (
        <label className="product__edition">
          <span>Издание</span>
          <select
            value={editionId}
            onChange={(event) => setEditionId(event.target.value)}
            onClick={(event) => event.stopPropagation()}
          >
            {editions.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <div className="product__body product__body--price">
        {prices.priceRub ? (
          <p className="product__price">
            {prices.priceRub} ₽
            {prices.compareAtRub ? <s className="product__price-old">{prices.compareAtRub} ₽</s> : null}
          </p>
        ) : null}
        <div className="product__price">
          <StarIcon size={18} />
          <span className="product__price-value">{prices.priceStars}</span>
          {prices.compareAtStars ? <s className="product__price-old">{prices.compareAtStars}</s> : null}
        </div>
      </div>
    </article>
  );
}
